import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Global state for floating "Messenger-style" chat windows that persist across
 * route changes (rendered at <App /> level, outside <Routes/>).
 *
 * Each chat has:
 *   {
 *     key:     string  // unique id, used for open/close/minimize ops
 *     type:    'market' | 'match' | 'friend'
 *     title:   string
 *     subtitle?: string
 *     avatar?: string
 *     params:  Record<string, any>  // type-specific (ad_id, match_id, friendship_id, ...)
 *     minimized: boolean
 *   }
 */
const FloatingChatContext = createContext(null);

export const FloatingChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);

  const openChat = useCallback((spec) => {
    if (!spec?.key) return;
    setChats((prev) => {
      const existing = prev.find((c) => c.key === spec.key);
      if (existing) {
        // Already open → expand it & move to front
        return [
          { ...existing, ...spec, minimized: false },
          ...prev.filter((c) => c.key !== spec.key),
        ];
      }
      // Cap to 3 simultaneously expanded windows; older expanded ones get minimized
      const next = [{ ...spec, minimized: false }, ...prev];
      let expandedCount = 0;
      return next.map((c) => {
        if (c.minimized) return c;
        expandedCount += 1;
        return expandedCount > 3 ? { ...c, minimized: true } : c;
      });
    });
  }, []);

  const closeChat = useCallback((key) => {
    setChats((prev) => prev.filter((c) => c.key !== key));
  }, []);

  const toggleMinimize = useCallback((key) => {
    setChats((prev) => prev.map((c) => (c.key === key ? { ...c, minimized: !c.minimized } : c)));
  }, []);

  return (
    <FloatingChatContext.Provider value={{ chats, openChat, closeChat, toggleMinimize }}>
      {children}
    </FloatingChatContext.Provider>
  );
};

export const useFloatingChat = () => {
  const ctx = useContext(FloatingChatContext);
  if (!ctx) throw new Error('useFloatingChat must be used inside FloatingChatProvider');
  return ctx;
};
